import { defineStore } from 'pinia'
import {
  getMembers,
  getTrainingLevels,
  getUser,
  getUserPreferences,
  patchProfile,
  patchUserPreferences,
  refreshProfile,
} from '@/services/UserService'
import type { Preferences, TrainingLevel, User, UserAPIListItem } from '#/User'
import { paginationObjetEmpty } from '@/stores/storeUtils'
import type { PaginationObject } from '#/index'
import type { QTableProps } from 'quasar'
import authority from '@/services/AuthService'

export interface UserStateStore {
  members: UserAPIListItem[]
  membersPagination: PaginationObject
  preferences: {
    isLoaded: boolean
    data: Preferences
  }
  trainingLevels: TrainingLevel[]
  user: User
}

export const emptyUser = (): User => ({
  academicYear: '',
  bibliography: '',
  disciplines: [],
  email: '',
  firstName: '',
  id: '',
  institution: '',
  isAdmin: false,
  lastName: '',
  publicProfile: false,
  service: '',
  status: 'EXTERNAL',
  trainingLevel: 0,
  username: '',
})

export const useUserStore = defineStore('User', {
  actions: {
    async loadStoryMembers(storyId: string, page = 1, rowsPerPage = 10): Promise<void> {
      if (
        this.membersPagination.itemPaginatedId !== storyId ||
        this.membersPagination.page !== page ||
        this.membersPagination.rowsPerPage !== rowsPerPage
      ) {
        const {
          count: rowsNumber,
          next,
          previous,
          results,
        } = await getMembers({
          for_story: storyId,
          page,
          page_size: rowsPerPage,
        })
        const newPagination = { page, rowsNumber, rowsPerPage, next, previous }
        this.members = results
        this.membersPagination = {
          ...this.membersPagination,
          ...newPagination,
          itemPaginatedId: storyId,
        }
      }
    },
    async loadTrainingLevels(): Promise<void> {
      if (!this.trainingLevels.length) this.trainingLevels = await getTrainingLevels()
    },
    async loadUser(): Promise<void> {
      if (!this.user.id) this.user = await getUser()
    },
    async loadUserPreferences() {
      if (!this.preferences.isLoaded) {
        const fetchedUserPreferences = await getUserPreferences()
        authority.setLanguage(fetchedUserPreferences.language)
        this.preferences = {
          data: {
            ...this.preferences.data,
            ...fetchedUserPreferences,
          },
          isLoaded: true,
        }
      }
    },
    async updateInternalProfile(): Promise<void> {
      const user = await refreshProfile()
      this.user = { ...user }
    },
    async updateUser(profile: Partial<User>): Promise<void> {
      const user = await patchProfile(profile)
      this.user = { ...user }
    },
    async updateUserPreferences(preferences: Partial<Preferences>) {
      const userPreferences = {
        ...this.preferences.data,
        ...preferences,
      }
      const fetchedUserPreferences = await patchUserPreferences(userPreferences)
      this.preferences = {
        data: {
          ...this.preferences.data,
          ...fetchedUserPreferences,
        },
        isLoaded: true,
      }
    },
  },

  getters: {
    getMembersPagination(): QTableProps['pagination'] {
      return {
        page: this.membersPagination.page,
        rowsPerPage: this.membersPagination.rowsPerPage,
        rowsNumber: this.membersPagination.rowsNumber,
      }
    },
    isAdmin(state) {
      return state.user.isAdmin
    },
    isDarkUserPreference(state) {
      return state.preferences.data.appearance === 'dark'
    },
    isExternal(state): boolean {
      return state.user.status === 'EXTERNAL'
    },
    isManager(state) {
      // Commented on this line because business needs have changed, but not to forget
      // const { id, status } = state.user
      // return !!id && status !== 'EXTERNAL'
      const { id } = state.user
      return !!id
    },
    userDisplayName(state): string {
      return `${state.user.firstName} ${state.user.lastName}`
    },
    userFirstLetter(state): string {
      return state.user.firstName[0]
    },
  },

  state: (): UserStateStore => ({
    members: [],
    membersPagination: {
      ...paginationObjetEmpty(),
      itemPaginatedId: null,
    },
    preferences: {
      isLoaded: false,
      data: {
        appearance: 'light',
        language: 'fr',
      },
    },
    trainingLevels: [],
    user: emptyUser(),
  }),
})
